import React from "react";
import { Copy } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { formatPhoneNumber } from "@/util/phoneUtils";

interface ContactSectionProps {
  email?: string | null;
  phoneNumber?: string | null;
}

export const ContactSection: React.FC<ContactSectionProps> = ({
  email,
  phoneNumber,
}) => {
  const { t } = useTranslation();

  const handleCopyEmail = async () => {
    if (!email) return;

    try {
      await navigator.clipboard.writeText(email);
      toast.success(t("studentCard.contact.copySuccess"), {
        description: t("studentCard.contact.copyEmailSuccess"),
      });
    } catch (error) {
      console.error("Failed to copy email:", error);
      toast.error(t("global.error", { message: "Failed to copy email" }));
    }
  };

  const handleCopyPhone = async () => {
    if (!phoneNumber) return;

    try {
      await navigator.clipboard.writeText(phoneNumber);
      toast.success(t("studentCard.contact.copySuccess"), {
        description: t("studentCard.contact.copyPhoneSuccess"),
      });
    } catch (error) {
      console.error("Failed to copy phone:", error);
      toast.error(t("global.error", { message: "Failed to copy phone" }));
    }
  };

  const formattedPhone = formatPhoneNumber(phoneNumber);

  return (
    <div className="border-t border-border p-6">
      <h2 className="mb-4 text-[16px] leading-tight font-semibold">
        {t("studentCard.contact.title")}
      </h2>
      <ul className="space-y-6 text-sm">
        <li className="flex items-center justify-between">
          <span className="text-muted-foreground">
            {t("studentCard.contact.email")}
          </span>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-foreground">
              {email || t("studentCard.contact.noEmail")}
            </span>
            {email && (
              <button
                onClick={handleCopyEmail}
                className="flex items-center justify-center rounded-md p-1 transition-colors hover:bg-gray-100"
                aria-label={t("studentCard.contact.copyButton")}
              >
                <Copy className="h-4 w-4 text-muted-foreground" />
              </button>
            )}
          </div>
        </li>
        <li className="flex items-center justify-between">
          <span className="text-muted-foreground">
            {t("studentCard.contact.phone")}
          </span>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-foreground">
              {formattedPhone || t("studentCard.contact.noPhone")}
            </span>
            {formattedPhone && (
              <button
                onClick={handleCopyPhone}
                className="flex items-center justify-center rounded-md p-1 transition-colors hover:bg-gray-100"
                aria-label={t("studentCard.contact.copyButton")}
              >
                <Copy className="h-4 w-4 text-muted-foreground" />
              </button>
            )}
          </div>
        </li>
      </ul>
    </div>
  );
};
